import { useMutation } from "@tanstack/react-query";
import {
  ArrowRightIcon,
  CartIcon,
  Divider,
  GKButton,
  GKModal,
  Grid2,
  Typography,
  WarningIcon,
} from "components";
import { useFetchQuery } from "hooks/useQueries";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { moneyCommaSeparator } from "utils";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";
import NumberSpinner from "./NumberSpinner";

const AdvisorLimitModal = ({
  portfolioReachLimit,
  setPortfolioReachLimit,
}: {
  portfolioReachLimit: boolean;
  setPortfolioReachLimit: (value: boolean) => void;
}) => {
  const form = useForm({
    defaultValues: {
      portfolioCount: 10,
    },
  });

  const { data: limitsResponse, isFetching } = useFetchQuery({
    route: `/portfolio-top-up/limits/`,
    key: ["GET_PORTFOLIO_TOP_UP_LIMITS"],
  });

  const limits: any = (limitsResponse as any)?.data?.data;

  const mutate = useMutation({
    mutationFn: (data: { portfolio_count: number }) =>
      axiosInstance.post(`/portfolio-top-up/`, data),
    onSuccess: (response: any) => {
      refetchQuery("GET_PORTFOLIO_TOP_UP_LIMITS");
      setPortfolioReachLimit(false);

      const paymentUrl = response.data?.data?.link;

      if (paymentUrl && typeof paymentUrl === "string") {
        const link = document.createElement("a");
        link.href = paymentUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
  });

  const calculationData: any = useMemo(() => {
    if (!limits?.forCalculations) {
      return null;
    }
    const perPortfolioCharges = limits?.forCalculations?.perPortfolioCharges;
    const renewalDate = limits?.forCalculations?.renewalDate;
    // const billingDate = new Date();
    const companyState = "GUJARAT";
    const clientState = limits?.forCalculations?.state || "GUJARAT";

    // GST Constants
    const GST_PERCENTAGE = {
      CGST: 9,
      SGST: 9,
      IGST: 18,
    };

    const renewal = new Date(renewalDate);
    renewal.setHours(0, 0, 0, 0);

    const billingDateNew = new Date();
    billingDateNew.setHours(0, 0, 0, 0);

    const oneDay = 1000 * 60 * 60 * 24;
    const daysDiff = Math.abs(
      // (renewal.getTime() - billingDate.getTime()) / oneDay,
      (renewal.getTime() - billingDateNew.getTime()) / oneDay,
    );

    const perPortfolioChargeCalc = Number(
      ((daysDiff / 365) * perPortfolioCharges).toFixed(2),
    );

    const portfolioAmount = Number(
      (form.watch("portfolioCount") * perPortfolioChargeCalc).toFixed(2),
    );

    const isSameState =
      clientState?.trim().toLowerCase() === companyState?.trim().toLowerCase();

    const taxData = {
      cgst: 0,
      sgst: 0,
      igst: 0,
      totalAmount: portfolioAmount,
      isSameState,
    };

    if (!companyState || !clientState) {
      return 0;
    }

    if (taxData.isSameState) {
      taxData.cgst = Number(
        ((portfolioAmount * GST_PERCENTAGE.CGST) / 100).toFixed(2),
      );
      taxData.sgst = Number(
        ((portfolioAmount * GST_PERCENTAGE.SGST) / 100).toFixed(2),
      );
    } else {
      taxData.igst = Number(
        ((portfolioAmount * GST_PERCENTAGE.IGST) / 100).toFixed(2),
      );
    }

    const netAmount =
      portfolioAmount + taxData.cgst + taxData.sgst + taxData.igst;

    return {
      daysDiff,
      perPortfolioChargeCalc,
      portfolioAmount,
      ...taxData,
      netAmount: Number(netAmount.toFixed(2)),
    };
  }, [form.watch("portfolioCount"), limits?.forCalculations]);

  console.log(calculationData, "calculationData");

  return (
    <GKModal
      loading={isFetching}
      modalTitle="Advisor Limit Reached"
      open={portfolioReachLimit}
      setOpen={setPortfolioReachLimit}
      width={"370px"}
      footer={
        <GKButton
          fullWidth
          variant="contained"
          startIcon={<CartIcon />}
          endIcon={<ArrowRightIcon />}
          onClick={() => {
            const portfolioCount = form.getValues("portfolioCount");
            mutate.mutate({ portfolio_count: portfolioCount });
          }}
          loading={mutate.isLoading}
        >
          Buy Now
        </GKButton>
      }
    >
      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <Typography fontSize={12} fontWeight={500} color="var(--neutral-500)">
            To create more portfolios, purchase additional slots in packs of 10.
          </Typography>
        </Grid2>
        <Grid2
          size={12}
          display={"flex"}
          alignItems={"center"}
          gap={1}
          style={{
            backgroundColor: "var(--orange-100)",
            borderRadius: "4px",
            padding: "8px",
          }}
        >
          <Grid2 size={"auto"} display="flex" alignItems="center">
            <WarningIcon />
          </Grid2>
          <Grid2 size={"auto"}>
            <Typography
              fontSize={10}
              fontWeight={600}
              color="var(--orange-700)"
              letterSpacing={1}
              textTransform={"uppercase"}
            >
              {limits?.usedPortfolioCount}/{limits?.portfolioLimit} PORTFOLIO
              USED
            </Typography>
          </Grid2>
        </Grid2>
        <Grid2
          container
          size={12}
          p={2}
          pb={1.5}
          display={"flex"}
          justifyContent={"space-between"}
          style={{
            backgroundColor: "var(--grey-bg-100)",
            borderRadius: "8px",
          }}
        >
          <Grid2 size={"auto"}>
            <Typography fontSize={14} fontWeight={500} color="var(--black-900)">
              Add Portfolios
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={400}
              color="var(--neutral-500)"
            >
              (₹{calculationData?.perPortfolioChargeCalc || "0"} / Portfolio)
            </Typography>
          </Grid2>
          <Grid2 size={"auto"}>
            <Controller
              name="portfolioCount"
              control={form.control}
              defaultValue={10}
              render={({ field }) => (
                <NumberSpinner
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v);
                  }}
                  min={10}
                  max={100}
                />
              )}
            />
          </Grid2>
          <Grid2
            size={12}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              fontSize={12}
              fontWeight={400}
              color="var(--neutral-500)"
            >
              Prorata days: {calculationData?.daysDiff || 0}
            </Typography>

            <Typography
              fontSize={12}
              fontWeight={500}
              color="var(--neutral-500)"
            >
              ₹{moneyCommaSeparator(calculationData?.totalAmount || "0.00")}
            </Typography>
          </Grid2>
        </Grid2>
        <Grid2
          size={12}
          gap={1}
          display="flex"
          justifyContent="flex-end"
          flexDirection="column"
          pr={1.5}
          mt={1}
        >
          <Grid2
            size={7}
            marginLeft={"auto"}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"flex-end"}
            gap={1}
          >
            <Typography
              fontSize={12}
              fontWeight={400}
              color="var(--neutral-500)"
            >
              Total:
            </Typography>
            <Typography fontSize={12} fontWeight={400} color="var(--grey-900)">
              ₹{moneyCommaSeparator(calculationData?.totalAmount) || "0.00"}
            </Typography>
          </Grid2>
          {calculationData?.isSameState ? (
            <>
              <Grid2
                size={7}
                marginLeft={"auto"}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"flex-end"}
                gap={1}
              >
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color="var(--neutral-500)"
                >
                  CGST (9%):
                </Typography>
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color="var(--grey-900)"
                >
                  ₹{calculationData?.cgst?.toFixed(2) || "0.00"}
                </Typography>
              </Grid2>
              <Grid2
                size={7}
                marginLeft={"auto"}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"flex-end"}
                gap={1}
              >
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color="var(--neutral-500)"
                >
                  SGST (9%):
                </Typography>
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color="var(--grey-900)"
                >
                  ₹{calculationData?.sgst?.toFixed(2) || "0.00"}
                </Typography>
              </Grid2>
            </>
          ) : (
            <Grid2
              size={7}
              marginLeft={"auto"}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"flex-end"}
              gap={1}
            >
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--neutral-500)"
              >
                IGST (18%):
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--grey-900)"
              >
                ₹{calculationData?.igst?.toFixed(2) || "0.00"}
              </Typography>
            </Grid2>
          )}
          <Divider style={{ width: "60%", marginLeft: "auto" }} />
          <Grid2
            size={7}
            marginLeft={"auto"}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"flex-end"}
            gap={1}
            mb={1}
          >
            <Typography
              fontSize={12}
              fontWeight={400}
              color="var(--neutral-500)"
            >
              Grand Total
            </Typography>
            <Typography fontSize={14} fontWeight={700} color="var(--black-900)">
              ₹{moneyCommaSeparator(calculationData?.netAmount) || "0.00"}
            </Typography>
          </Grid2>
        </Grid2>
      </Grid2>
    </GKModal>
  );
};

export default AdvisorLimitModal;
